import React, { Fragment, useCallback, useEffect } from "react";

import AsideFilter from "./comps/AsideFilter";
import MobileFilter from "./comps/MobileFilter";
import TopbarFilter from "./comps/TopbarFilter";



import { getBreakPoint, useScreenWidth } from "oolib";

import { handleOnChange, convertToInput } from "./utils/handleOnChange";
import { useSavedFilterConfig } from "./utils/useSavedFilterConfig";
import { useShowFilterModuleV3 } from "./hooks/useShowFilterModuleV3";
/**
 * Why are presetActiveFilters being passed into FilterModules?
 *
 * these are only meant to be considered while parsing display conditions,
 * because the parent that is using this filterModule,
 * HAS the presetActiveFilters applied along with activeFilters.
 *
 * The reason why we parse the display conditions inside the FilterModule is
 * because that way it becomes a global feature rather than specific to one
 * particular module.
 */

const FilterModule = ({
  presetActiveFilters = [],
  activeFilters = [],
  setActiveFilters,
  filterConfig: _filterConfig,
  /**
   * //OPTIONAL: about contentTypes prop
   * the reason this is passed thru, is so that the FilterInput component
   * inside Topbar/Aside/Mobile Filter knows what contenttypes are
   * being filtered, and hence fetch only the tags that have these
   * content types as tagged resources.
   *
   * This should not be used all the time. For example, if your filters
   * are using 'rollup', then obv this content type that is being filtered
   * wont exist as a taggedResource on the tag doc (since the rollup resource
   * will exist instead). In such cases, best not to pass this contentTypes prop only
   *
   */

  contentTypes,
  orientation = "sidebar", //alt topbar - but topbar is deprecated
  totalResult,
  usedIn,
  mobileHeader,
  isMobile,
  style,
  showClearAllButton = true, // Option to disable clear all filters button
  debug,
  storeKey,
  skipLocalFilterSave = false,
  // External control props for LightboxFilterHeader
  addFilterAnchorEl,
  addFilterLightboxOpen,
  onAddFilterLightboxOpenChange,
  onClearFilters,
  onHasFiltersToClear,
  disableInlinePadding,
  // Hide the "Filters" title row in LightboxFilterHeader (keeps Clear / Add buttons).
  hideFilterModuleHeader = false,
  // Override the "Add Filter" button label in LightboxFilterHeader.
  addFilterButtonText = "Add Filter",
  // Suppress the per-block Match Any/All select in applied-filter blocks.
  hideIntraFilterOperatorSelect = false,
  // When true, applied-filter blocks render ALL their chip values (no
  // 2-line truncation + "+N" overflow toggle). Used by the color-code
  // breakdown picker where every selected chip needs to stay visible.
  noAppliedValueChipsTruncation = false,
  // True when this surface renders ANNOTATION filters. Such surfaces only get the
  // new (V3) filter UI when the tags_subThemes_themes annotation hierarchy exists;
  // on a flexible-annotation tenant they fall back to the old UI. See
  // useShowFilterModuleV3. Doc-only surfaces can omit this (defaults false).
  usesAnnoFilters = false,
  // Override the V3 gate (useShowFilterModuleV3). Used by the FiltersPlayground
  // to toggle between old and new UI regardless of tenant config.
  _forceV3,
}) => {
  if (!skipLocalFilterSave && !usedIn) {
    throw new Error("`usedIn` prop is required if `skipLocalFilterSave` is not set to true");
  }
  
  const screenWidth = useScreenWidth();
  const { savedFilterConfig, setSavedFilterConfig, setSavedActiveFilters } = useSavedFilterConfig({ 
    module: usedIn, 
    activeFilters, 
    setActiveFilters, 
    filterConfig : _filterConfig, 
    storeKey, 
    skipLocalFilterSave 
  });
  // Expose clear function to parent via callback
  const clearFilters = useCallback(() => {
    if (activeFilters.length) {
      setActiveFilters([]);
    } else {
      setSavedFilterConfig([]);
      setSavedActiveFilters([]);
    }
  }, [activeFilters]);

  const hasFiltersToClear = activeFilters.length > 0 || savedFilterConfig.length > 0;

  useEffect(() => {
    onClearFilters?.(clearFilters);
  }, [clearFilters, onClearFilters]);

  useEffect(() => {
    onHasFiltersToClear?.(hasFiltersToClear);
    // Unmounting (e.g. a widget dropping to one dimension hides its filter
    // section) must not leave the host's clear button stuck on.
    return () => onHasFiltersToClear?.(false);
  }, [hasFiltersToClear, onHasFiltersToClear]);

  const { showFilterModuleV3: _autoV3 } = useShowFilterModuleV3({ usesAnnoFilters });
  const showFilterModuleV3 = _forceV3 !== undefined ? _forceV3 : _autoV3;
  const filterConfig = [
    // {
    //   filterId: 'datetypetest',
    //   display: 'Date Type Test',
    //   source: {
    //     filterType: 'dateRangeType'
    //   },
    //   target: {
    //     filterType: 'dateRangeType',
    //     valuePath: 'kp_date_published'
    //   }
    // },
    ..._filterConfig

  ]

  // `meta` carries where the change came from (only the Add Filter picker stamps
  // it today, as { fromPicker: true }) — handleOnChange needs it to decide whether
  // a multi-path attribute keeps fanning out across its directions.
  const _handleOnChange = ({ id, value, meta }) => {
    handleOnChange({
      id,
      value,
      activeFilters,
      setActiveFilters,
      filterConfig,
      usedIn,
      meta,
      showFilterModuleV3,
    })
  };

  // In new lightbox UI, only show clear all button when filters exist. In old UI, showClearAllButton.
  const _showClearAllButton = showFilterModuleV3 
    ? showClearAllButton && activeFilters.length > 0 || savedFilterConfig.length > 0 && showFilterModuleV3 
    : showClearAllButton;

  return (
    <Fragment>
      {orientation === "sidebar" ? (
        <AsideFilter
          selectedValues={convertToInput(activeFilters)}
          filterConfig={filterConfig}
          onChange={_handleOnChange}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          presetActiveFilters={presetActiveFilters}
          contentTypes={contentTypes}
          mobileHeader={mobileHeader}
          isMobile={isMobile}
          style={style}
          showClearAllButton={_showClearAllButton}
          debug={debug}
          savedFilterConfig={savedFilterConfig}
          setSavedFilterConfig={setSavedFilterConfig}
          setSavedActiveFilters={setSavedActiveFilters}
          showFilterModuleV3 ={showFilterModuleV3 }
          addFilterAnchorEl={addFilterAnchorEl}
          addFilterLightboxOpen={addFilterLightboxOpen}
          onAddFilterLightboxOpenChange={onAddFilterLightboxOpenChange}
          disableInlinePadding={disableInlinePadding}
          skipLocalFilterSave={skipLocalFilterSave}
          hideFilterModuleHeader={hideFilterModuleHeader}
          addFilterButtonText={addFilterButtonText}
          hideIntraFilterOperatorSelect={hideIntraFilterOperatorSelect}
          noAppliedValueChipsTruncation={noAppliedValueChipsTruncation}
        />
      ) : screenWidth >= getBreakPoint("md") ? (
        <TopbarFilter
          selectedValues={convertToInput(activeFilters)}
          onChange={_handleOnChange}
          filterConfig={filterConfig}
          activeFilters={activeFilters}
          presetActiveFilters={presetActiveFilters}
          contentTypes={contentTypes}
          setActiveFilters={setActiveFilters}
        />
      ) : (
        <MobileFilter
          onChange={_handleOnChange}
          selectedValues={convertToInput(activeFilters)}
          totalResult={totalResult === "loading" ? "..." : totalResult ?? 0}
          filterConfig={filterConfig}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          presetActiveFilters={presetActiveFilters}
          contentTypes={contentTypes}
          showClearAllButton={showClearAllButton}
        />
      )}
    </Fragment>
  );
};

export default FilterModule;
